import Phaser from 'phaser';
import { track } from '../services/analytics';
import { resumeAudio, startMusic } from '../services/audio';
import { ensureTierTextures, textureKey, TIERS } from '../game/shapes';
import { getAllTimeMergeCounts } from '../game/score';
import { ScoreState } from '../game/score';
import { fmt } from '../util/format';
import { addNeonBackdrop } from '../util/glow';
import { createButton, createIconButton } from '../util/button';
import { drawGear } from '../util/glyphs';
import { activePalette, css } from '../util/palette';

const DPR = typeof window !== 'undefined' ? window.devicePixelRatio || 1 : 1;
const FONT = 'Fredoka, system-ui, -apple-system, sans-serif';

/** CSS width >= 700 px is treated as tablet (mirrors GameScene's WIDE_SCREEN_MIN). */
const TABLET_MIN = 700;

export class MainMenuScene extends Phaser.Scene {
  static readonly KEY = 'MainMenuScene';

  constructor() {
    super({ key: MainMenuScene.KEY });
  }

  create(): void {
    ensureTierTextures(this);
    track('main_menu_shown', { version: __APP_VERSION__ });

    const { width, height } = this.scale;
    const cx = width / 2;
    const cy = height / 2;
    const pal = activePalette();

    // Tablet: scale factor applied to fonts and layout offsets.
    const isTablet = width >= TABLET_MIN;
    // Scale factor: 1.0 on phone, up to 1.75 on large tablets. Height-guarded
    // so short-but-wide desktop windows don't push the title off-screen.
    const tScale = isTablet
      ? Math.max(1.0, Math.min(1.0 + (width - TABLET_MIN) / 280, height / 780, 1.75))
      : 1.0;

    // Soft fade up from the backdrop's bottom tone for a gentle entrance.
    this.cameras.main.fadeIn(220, 7, 6, 15);

    // Neon-candy backdrop, glow behind the wordmark. (Shared; see util/glow.ts.)
    addNeonBackdrop(this, { glowX: cx, glowY: cy - 110, glowRadius: Math.min(width, height) * 0.6, bokeh: true });

    this.add
      .text(cx, cy - Math.round(197 * tScale), 'SHMERGE', {
        fontFamily: FONT, fontSize: `${Math.round(76 * tScale)}px`, fontStyle: 'bold', color: css(pal.text), resolution: DPR,
      })
      .setOrigin(0.5);

    // Task 1: tagline — "if you dare" in parentheses, no trailing period.
    this.add
      .text(cx, cy - Math.round(132 * tScale), 'Drop. Merge. Rotate (if you dare)', {
        fontFamily: FONT, fontSize: `${Math.round(18 * tScale)}px`, color: css(pal.textMuted), resolution: DPR,
      })
      .setOrigin(0.5);

    this.renderShapeProgression(cx, cy - Math.round(97 * tScale), width, tScale);

    // PLAY button — taller on tablet.
    const buttonWidth = Math.min(width - 32, Math.round(360 * tScale));
    const playH = Math.round(80 * tScale);
    createButton(this, {
      x: cx, y: cy + Math.round(10 * tScale), width: buttonWidth, height: playH,
      label: 'PLAY', fontSize: Math.round(28 * tScale), pulse: true,
      onClick: () => {
        resumeAudio();
        void startMusic();
        track('menu_button_tapped');
        this.scene.start('GameScene');
      },
    });

    // Top-right chrome: settings icon chip.
    let topInset = 0;
    try {
      const v = getComputedStyle(document.documentElement).getPropertyValue('--sat').trim();
      const n = parseFloat(v);
      if (Number.isFinite(n) && n >= 0) topInset = n;
    } catch {}
    const iconSize = 46;
    const iconY = 38 + topInset + iconSize / 2;
    const settingsX = width - 8 - iconSize / 2;

    createIconButton(this, {
      x: settingsX, y: iconY, size: iconSize, caption: 'SETTINGS',
      draw: (g, c) => drawGear(g, 0, 0, c, 0.92, pal.surface),
      onClick: () => {
        resumeAudio();
        this.scene.start('SettingsScene');
      },
    });

    this.add
      .text(cx, height - 24, `v${__APP_VERSION__}`, {
        fontFamily: FONT, fontSize: '11px', color: css(pal.textFaint), resolution: DPR,
      })
      .setOrigin(0.5);

    // Personal high score
    const personalBest = new ScoreState().getHighScore();
    this.add
      .text(cx, cy + Math.round(120 * tScale), 'BEST SCORE', {
        fontFamily: FONT, fontSize: `${Math.round(20 * tScale)}px`, fontStyle: 'bold', color: css(pal.textLabel), resolution: DPR,
      })
      .setOrigin(0.5);
    this.add
      .text(cx, cy + Math.round(156 * tScale), fmt(personalBest), {
        fontFamily: FONT, fontSize: `${Math.round(52 * tScale)}px`, fontStyle: 'bold', color: css(pal.text), resolution: DPR,
      })
      .setOrigin(0.5);

    // Secondary row — LEADERBOARD + HOW TO PLAY side by side. Capped so the
    // row keeps clear daylight above the version label on short windows.
    const rowW = Math.min(width - 32, Math.round(360 * tScale));
    const secH = Math.round(46 * tScale);
    const lbBtnY = Math.min(
      Math.round((cy + Math.round(200 * tScale) + height) / 2) - 12,
      height - 56 - Math.round(secH / 2),
    );
    const secFontSize = Math.round(13 * tScale);
    const halfW = Math.round((rowW - 12) / 2);
    createButton(this, {
      x: cx - halfW / 2 - 6, y: lbBtnY, width: halfW, height: secH,
      label: 'LEADERBOARD', fontSize: secFontSize, variant: 'secondary', color: pal.primary, textColor: css(pal.primary),
      onClick: () => {
        resumeAudio();
        this.scene.start('LeaderboardScene', { returnTo: 'MainMenuScene' });
      },
    });
    createButton(this, {
      x: cx + halfW / 2 + 6, y: lbBtnY, width: halfW, height: secH,
      label: 'HOW TO PLAY', fontSize: secFontSize, variant: 'secondary', color: pal.secondary, textColor: css(pal.secondary),
      onClick: () => {
        resumeAudio();
        track('how_to_play_opened');
        this.showHowToPlay(width, height, cx, cy);
      },
    });
  }

  showHowToPlay(width: number, height: number, cx: number, cy: number): void {
    const pal = activePalette();

    // Task 4: on tablets, make the modal substantially larger.
    const isTablet = width >= TABLET_MIN;
    const mScale = isTablet ? Math.min(1.0 + (width - TABLET_MIN) / 660, 1.6) : 1.0;

    const cardW = isTablet
      ? Math.min(width - 80, Math.round(340 * mScale))
      : Math.min(width - 40, 340);
    const cardH = isTablet ? Math.round(524 * mScale) : 524;
    const cardX = cx - cardW / 2;
    const cardY = cy - cardH / 2;

    const dim = this.add.rectangle(0, 0, width, height, 0x000000, 0.75)
      .setOrigin(0).setDepth(200).setInteractive();
    const card = this.add.graphics().setDepth(201);
    card.fillStyle(pal.surfaceDeep, 1);
    card.fillRoundedRect(cardX, cardY, cardW, cardH, 20);
    card.lineStyle(2, pal.button, 0.7);
    card.strokeRoundedRect(cardX, cardY, cardW, cardH, 20);

    const title = this.add.text(cx, cardY + Math.round(34 * mScale), 'HOW TO PLAY', {
      fontFamily: FONT, fontSize: `${Math.round(22 * mScale)}px`, fontStyle: 'bold', color: css(pal.text), resolution: DPR,
    }).setOrigin(0.5).setDepth(202);

    // Shape progression strip
    const shapeObjs = this.renderShapeProgressionTracked(cx, cardY + Math.round(76 * mScale), cardW - 32);

    const steps = [
      ['Drop shapes', 'Tap (or hold & aim) to drop into the container'],
      ['Merge', 'Two identical shapes touching → combine into the next tier up'],
      ['Steer', 'Tilt your phone or drag the wheel to aim while falling'],
      ['Game over', 'Pile crosses the line, or a shape escapes the container'],
    ];

    const stepObjects: Phaser.GameObjects.Text[] = [];
    let y = cardY + Math.round(122 * mScale);
    const headSize = Math.round(21 * mScale);
    const bodySize = Math.round(18 * mScale);
    const headGap = Math.round(26 * mScale);
    const bodyGap = Math.round(60 * mScale);
    for (const [head, body] of steps) {
      stepObjects.push(this.add.text(cardX + Math.round(20 * mScale), y, head, {
        fontFamily: FONT, fontSize: `${headSize}px`, fontStyle: 'bold', color: css(pal.primary), resolution: DPR,
      }).setDepth(202));
      y += headGap;
      stepObjects.push(this.add.text(cardX + Math.round(20 * mScale), y, body, {
        fontFamily: FONT, fontSize: `${bodySize}px`, color: css(pal.textSoft), resolution: DPR,
        wordWrap: { width: cardW - Math.round(40 * mScale) },
      }).setDepth(202));
      y += bodyGap;
    }

    // Task 2: GOT IT button — use createButton (matches PLAY / LEADERBOARD / HOW TO PLAY style)
    // with comfortable bottom padding inside the card.
    const gotItBtnW = Math.round(160 * mScale);
    const gotItBtnH = Math.round(46 * mScale);
    // Position: cardY + cardH - padding - half button height
    const gotItPad = Math.round(28 * mScale);
    const gotItBtnY = cardY + cardH - gotItPad - gotItBtnH / 2;

    const gotItBtn = createButton(this, {
      x: cx, y: gotItBtnY, width: gotItBtnW, height: gotItBtnH,
      label: 'GOT IT', fontSize: Math.round(16 * mScale), variant: 'primary',
      depth: 202,
      onClick: closeAll,
    });

    function closeAll() {
      [dim, card, title, gotItBtn, ...stepObjects, ...shapeObjs].forEach(o => o.destroy());
    }
    dim.on('pointerup', closeAll);
  }

  private renderShapeProgressionTracked(cx: number, y: number, availableWidth: number): Phaser.GameObjects.Image[] {
    ensureTierTextures(this);
    const tierCount = TIERS.length;
    const MIN_SIZE = 16;
    const MAX_SIZE = 42;
    const gap = 5;
    const sizes: number[] = [];
    let totalWidth = 0;
    for (let i = 0; i < tierCount; i++) {
      const t = tierCount > 1 ? i / (tierCount - 1) : 0;
      sizes.push(MIN_SIZE + t * (MAX_SIZE - MIN_SIZE));
      totalWidth += sizes[i];
    }
    totalWidth += gap * (tierCount - 1);
    const fitScale = Math.min(1, availableWidth / totalWidth);
    let x = cx - (totalWidth * fitScale) / 2;
    const objs: Phaser.GameObjects.Image[] = [];
    for (let i = 0; i < tierCount; i++) {
      const tier = TIERS[i];
      const cellSize = sizes[i] * fitScale;
      const scale = cellSize / (tier.radius * 2 + 6);
      // Triangle (tier 0) sits visually high due to its point — nudge it down.
      const yOffset = tier.kind === 'triangle' ? cellSize * 0.12 : 0;
      objs.push(
        this.add.image(x + cellSize / 2, y + yOffset, textureKey(i))
          .setOrigin(0.5).setScale(scale).setAlpha(0.95).setDepth(202)
      );
      x += cellSize + gap * fitScale;
    }
    return objs;
  }

  private renderShapeProgression(cx: number, y: number, width: number, tScale = 1.0): void {
    ensureTierTextures(this);
    const counts = getAllTimeMergeCounts();
    const tierCount = TIERS.length;
    const MIN_SIZE = Math.round(14 * tScale);
    const MAX_SIZE = Math.round(38 * tScale);
    const gap = Math.round(4 * tScale);
    const sizes: number[] = [];
    let totalWidth = 0;
    for (let i = 0; i < tierCount; i++) {
      const t = tierCount > 1 ? i / (tierCount - 1) : 0;
      const size = MIN_SIZE + t * (MAX_SIZE - MIN_SIZE);
      sizes.push(size);
      totalWidth += size;
    }
    totalWidth += gap * (tierCount - 1);
    const usable = Math.min(width - 24, totalWidth);
    const fitScale = usable / totalWidth;
    let x = cx - (totalWidth * fitScale) / 2;
    const countY = y + (MAX_SIZE * fitScale) / 2 + 8;
    for (let i = 0; i < tierCount; i++) {
      const tier = TIERS[i];
      const cellSize = sizes[i] * fitScale;
      const textureSize = tier.radius * 2 + 6;
      const scale = cellSize / textureSize;
      const img = this.add
        .image(x + cellSize / 2, y, textureKey(i))
        .setOrigin(0.5).setScale(scale).setAlpha(0.95);
      // Staggered gentle float so the evolution chain feels alive — a wave
      // rippling left to right across the tiers.
      this.tweens.add({
        targets: img,
        y: y - 5,
        duration: 1200,
        delay: i * 90,
        yoyo: true,
        repeat: -1,
        ease: 'Sine.easeInOut',
      });
      // Merge-created count beneath each shape; triangle excluded, KING included.
      if (i > 0) {
        const count = counts[i] ?? 0;
        const countFontSize = Math.max(9, Math.round(9 * tScale));
        this.add.text(x + cellSize / 2, countY, `${count}`, {
          fontFamily: FONT, fontSize: `${countFontSize}px`, fontStyle: 'bold', color: css(activePalette().textSoft), resolution: DPR,
        }).setOrigin(0.5).setAlpha(count > 0 ? 1 : 0.35);
      }
      x += cellSize + gap * fitScale;
    }
  }


}
